import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import DigitalCard from './DigitalCard';
import { Wallet, Gift, TrendingUp, Crown, ArrowRight, AlertTriangle, CheckCircle, Loader, ShieldAlert } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const VIP_LEVELS = {
  diavelico: { name: 'Diavélico', next: 'silver', nextAmount: 200 },
  silver: { name: 'Silver', next: 'gold', nextAmount: 500 },
  gold: { name: 'Gold', next: 'diamond', nextAmount: 1000 },
  diamond: { name: 'Diamond', next: null, nextAmount: null },
};

export default function DashboardPage() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [recentTxns, setRecentTxns] = useState([]);
  const [membership, setMembership] = useState(null);
  const [payingMembership, setPayingMembership] = useState(false);
  const [membershipPayStatus, setMembershipPayStatus] = useState(null);

  const fetchMembership = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/membership/status`, { withCredentials: true });
      setMembership(res.data);
    } catch {}
  }, []);

  // Poll membership payment if returning from Stripe
  useEffect(() => {
    const sessionId = searchParams.get('membership_session_id');
    if (!sessionId) return;
    
    setMembershipPayStatus('checking');
    let attempts = 0;
    const poll = async () => {
      if (attempts >= 8) { setMembershipPayStatus('error'); return; }
      try {
        const res = await axios.get(`${API}/membership/pay/status/${sessionId}`, { withCredentials: true });
        if (res.data.payment_status === 'paid') {
          setMembershipPayStatus('success');
          await refreshUser();
          fetchMembership();
          setSearchParams({});
          return;
        }
        if (res.data.status === 'expired') { setMembershipPayStatus('error'); setSearchParams({}); return; }
      } catch {}
      attempts++;
      setTimeout(poll, 2000);
    };
    poll();
  }, [searchParams, refreshUser, fetchMembership, setSearchParams]);

  useEffect(() => {
    fetchMembership();
    (async () => {
      try {
        const res = await axios.get(`${API}/transactions`, { withCredentials: true });
        setRecentTxns((res.data.transactions || []).slice(0, 5));
      } catch {}
    })();
  }, [fetchMembership]);

  const handlePayMembership = async () => {
    setPayingMembership(true);
    try {
      const originUrl = window.location.origin;
      const res = await axios.post(`${API}/membership/pay`, { origin_url: originUrl }, { withCredentials: true });
      if (res.data.url) window.location.href = res.data.url;
    } catch (err) {
      alert(err.response?.data?.detail || 'Error al procesar el pago');
    } finally {
      setPayingMembership(false);
    }
  };

  const vipInfo = VIP_LEVELS[user?.vip_level] || VIP_LEVELS.diavelico;
  const progress = vipInfo.nextAmount ? Math.min((user?.total_spent || 0) / vipInfo.nextAmount * 100, 100) : 100;
  const isAdmin = user?.role === 'admin';

  return (
    <div className="p-6 md:p-10 pb-24 md:pb-10 max-w-5xl" data-testid="dashboard-page">
      <div className="mb-10 animate-fade-in-up">
        <h1 className="font-heading font-extrabold text-3xl md:text-4xl tracking-tight uppercase text-white">
          Hola, {user?.name?.split(' ')[0]}
        </h1>
        <p className="text-zinc-500 mt-2">Bienvenido a tu espacio Diavélico</p>
      </div>

      {/* Membership payment status banners */}
      {membershipPayStatus === 'checking' && (
        <div className="bg-yellow-900/20 border border-yellow-600/30 p-4 mb-6 flex items-center gap-3 animate-fade-in-up">
          <Loader className="w-5 h-5 text-yellow-400 animate-spin" />
          <p className="text-yellow-400 text-sm">Verificando tu pago de membresía...</p>
        </div>
      )}
      {membershipPayStatus === 'success' && (
        <div className="bg-green-900/20 border border-green-600/30 p-4 mb-6 flex items-center gap-3 animate-fade-in-up" data-testid="membership-success">
          <CheckCircle className="w-5 h-5 text-green-400" />
          <p className="text-green-400 text-sm font-semibold">¡Bienvenido al INFERNO! Tu membresía está activa.</p>
        </div>
      )}

      {/* Membership banner */}
      {membership && !isAdmin && (
        <>
          {/* Pending - never paid */}
          {membership.status === 'pending' && (
            <div className="bg-diavel-card border border-diavel-red/30 p-6 mb-8 animate-fade-in-up" data-testid="membership-pending-banner">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <ShieldAlert className="w-5 h-5 text-diavel-red" />
                    <h3 className="font-heading font-bold text-lg uppercase tracking-wider text-white">Activa tu Membresía</h3>
                  </div>
                  <p className="text-zinc-400 text-sm">
                    Para disfrutar de todas las ventajas del INFERNO (50% en tatuajes, referidos, prioridad...) necesitas activar tu membresía anual.
                  </p>
                </div>
                <button
                  onClick={handlePayMembership}
                  disabled={payingMembership}
                  data-testid="pay-membership-btn"
                  className="bg-diavel-red text-white font-bold uppercase tracking-wider py-3 px-8 hover:bg-red-700 transition-all duration-300 hover:shadow-[0_0_20px_rgba(196,30,30,0.4)] disabled:opacity-50 whitespace-nowrap flex items-center gap-2"
                >
                  {payingMembership ? <Loader className="w-4 h-4 animate-spin" /> : null}
                  Pagar {membership.membership_price}€/año
                </button>
              </div>
            </div>
          )}

          {/* Expired */}
          {membership.status === 'expired' && (
            <div className="bg-diavel-card border border-red-600/50 p-6 mb-8 animate-fade-in-up" data-testid="membership-expired-banner">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <AlertTriangle className="w-5 h-5 text-red-400" />
                    <h3 className="font-heading font-bold text-lg uppercase tracking-wider text-white">Membresía Expirada</h3>
                  </div>
                  <p className="text-zinc-400 text-sm">
                    Tu membresía ha expirado. Renuévala para seguir disfrutando de las ventajas del INFERNO.
                  </p>
                </div>
                <button
                  onClick={handlePayMembership}
                  disabled={payingMembership}
                  data-testid="renew-membership-btn"
                  className="bg-diavel-red text-white font-bold uppercase tracking-wider py-3 px-8 hover:bg-red-700 transition-all duration-300 hover:shadow-[0_0_20px_rgba(196,30,30,0.4)] disabled:opacity-50 whitespace-nowrap flex items-center gap-2"
                >
                  {payingMembership ? <Loader className="w-4 h-4 animate-spin" /> : null}
                  Renovar por {membership.current_price}€/año
                </button>
              </div>
            </div>
          )}

          {/* Active but needs renewal soon */}
          {membership.status === 'active' && membership.needs_renewal && !membership.is_expired && (
            <div className="bg-diavel-card border border-yellow-600/30 p-6 mb-8 animate-fade-in-up" data-testid="membership-renewal-banner">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <AlertTriangle className="w-5 h-5 text-yellow-400" />
                    <h3 className="font-heading font-bold text-lg uppercase tracking-wider text-white">Renovación Próxima</h3>
                  </div>
                  <p className="text-zinc-400 text-sm">
                    Tu membresía expira en <span className="text-yellow-400 font-semibold">{membership.days_remaining} días</span>. 
                    Renuévala ahora para mantener tu precio de {membership.membership_price}€/año.
                  </p>
                </div>
                <button
                  onClick={handlePayMembership}
                  disabled={payingMembership}
                  data-testid="early-renew-btn"
                  className="bg-yellow-600 text-black font-bold uppercase tracking-wider py-3 px-8 hover:bg-yellow-500 transition-all duration-300 disabled:opacity-50 whitespace-nowrap flex items-center gap-2"
                >
                  {payingMembership ? <Loader className="w-4 h-4 animate-spin" /> : null}
                  Renovar {membership.membership_price}€
                </button>
              </div>
            </div>
          )}

          {/* Active and fine */}
          {membership.status === 'active' && !membership.needs_renewal && (
            <div className="bg-diavel-card border border-green-600/20 p-4 mb-8 flex items-center gap-3 animate-fade-in-up" data-testid="membership-active-banner">
              <CheckCircle className="w-5 h-5 text-green-400" />
              <div>
                <p className="text-green-400 text-sm font-semibold">Membresía activa — {membership.membership_price}€/año</p>
                <p className="text-zinc-500 text-xs">
                  Expira: {membership.membership_expiry ? new Date(membership.membership_expiry).toLocaleDateString('es-ES', { day: '2-digit', month: 'long', year: 'numeric' }) : '—'} 
                  ({membership.days_remaining} días restantes)
                </p>
              </div>
            </div>
          )}
        </>
      )}

      {/* Digital Card */}
      <div className="mb-10 animate-fade-in-up animate-delay-100">
        <DigitalCard />
      </div>

      {/* Quick actions */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10 animate-fade-in-up animate-delay-200">
        <button onClick={() => navigate('/wallet')} data-testid="quick-recharge-btn"
          className="bg-diavel-card border border-white/5 p-5 text-left hover:border-red-900/30 transition-all duration-500 group">
          <Wallet className="w-5 h-5 text-diavel-red mb-3" />
          <p className="text-sm font-medium text-white">Recargar</p>
          <p className="text-xs text-zinc-600 mt-1">Añadir saldo</p>
        </button>
        <button onClick={() => navigate('/referral')} data-testid="quick-referral-btn"
          className="bg-diavel-card border border-white/5 p-5 text-left hover:border-red-900/30 transition-all duration-500 group">
          <Gift className="w-5 h-5 text-diavel-red mb-3" />
          <p className="text-sm font-medium text-white">Referidos</p>
          <p className="text-xs text-zinc-600 mt-1">Gana un 20%</p>
        </button>
        <button onClick={() => navigate('/wallet')} data-testid="quick-history-btn"
          className="bg-diavel-card border border-white/5 p-5 text-left hover:border-red-900/30 transition-all duration-500 group">
          <TrendingUp className="w-5 h-5 text-diavel-red mb-3" />
          <p className="text-sm font-medium text-white">Historial</p>
          <p className="text-xs text-zinc-600 mt-1">Movimientos</p>
        </button>
        <button onClick={() => navigate('/profile')} data-testid="quick-vip-btn"
          className="bg-diavel-card border border-white/5 p-5 text-left hover:border-red-900/30 transition-all duration-500 group">
          <Crown className="w-5 h-5 text-diavel-red mb-3" />
          <p className="text-sm font-medium text-white">Nivel VIP</p>
          <p className="text-xs text-zinc-600 mt-1">{vipInfo.name}</p>
        </button>
      </div>

      {/* VIP Progress */}
      <div className="bg-diavel-card border border-white/5 p-6 mb-10 animate-fade-in-up animate-delay-300">
        <div className="flex justify-between items-center mb-4">
          <h3 className="font-heading font-bold text-sm uppercase tracking-wider text-white">Progreso VIP</h3>
          <span className="font-mono text-xs tracking-wider uppercase text-zinc-500">
            {vipInfo.nextAmount ? `${(user?.total_spent || 0).toFixed(0)}€ / ${vipInfo.nextAmount}€` : 'Nivel máximo'}
          </span>
        </div>
        <div className="h-2 bg-zinc-900 overflow-hidden">
          <div className="h-full bg-diavel-red transition-all duration-1000" style={{ width: `${progress}%` }} />
        </div>
        {vipInfo.next && (
          <p className="text-xs text-zinc-600 mt-3">
            Te faltan <span className="text-white font-semibold">{(vipInfo.nextAmount - (user?.total_spent || 0)).toFixed(0)}€</span> para alcanzar el nivel{' '}
            <span className="text-diavel-red font-semibold">{VIP_LEVELS[vipInfo.next]?.name}</span>
          </p>
        )}
      </div>

      {/* Recent transactions */}
      <div className="bg-diavel-card border border-white/5 p-6 animate-fade-in-up animate-delay-400">
        <div className="flex justify-between items-center mb-6">
          <h3 className="font-heading font-bold text-sm uppercase tracking-wider text-white">Últimos movimientos</h3>
          <button onClick={() => navigate('/wallet')} className="text-xs text-diavel-red hover:text-red-400 flex items-center gap-1 transition-colors">
            Ver todos <ArrowRight className="w-3 h-3" />
          </button>
        </div>
        {recentTxns.length === 0 ? (
          <p className="text-zinc-600 text-sm">Aún no tienes movimientos</p>
        ) : (
          <div className="space-y-3">
            {recentTxns.map((txn, i) => (
              <div key={i} className="flex justify-between items-center py-3 border-b border-white/5 last:border-0">
                <div>
                  <p className="text-sm text-white">{txn.description}</p>
                  <p className="font-mono text-xs text-zinc-600">{new Date(txn.created_at).toLocaleDateString('es-ES')}</p>
                </div>
                <span className={`font-heading font-bold text-sm ${txn.amount >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                  {txn.amount >= 0 ? '+' : ''}{txn.amount.toFixed(2)}€
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
